import camelCase from 'lodash/camelCase';
import { useI18n } from '../../composables/i18n.composable';
import { infuse } from '../../ioc-config';
import {
  FilterCode,
  IProductFilterOptionDTO,
  ProductFilterTypes,
} from '../../models/product-filter/product-filter-base.model';
import ProductFilterPriceSlider from '../../models/product-filter/product-filter-price-slider.model';
import ProductFilterSlider from '../../models/product-filter/product-filter-slider.model';
import { getTranslationFallback } from '../../utils/translation.utils';
import { createSelectors } from '../store.util';
import { connectionName, LocationFilterTabs, ProductsState, productsStore } from './products.connection';

export type DropdownOption = {
  value: string;
  label: string;
  disabled?: boolean;
  testId?: string;
};

const selectors = {
  isLoading: (state: ProductsState) => state.loadingResults,

  selectedFilters: (state: ProductsState) => state.filters.filter((filter) => filter.isSelected()),

  selectedVisibleFilters: (state: ProductsState) => selectors.selectedFilters(state).filter((filter) => filter.visible),

  isAnyFilterSelected: (state: ProductsState) => state.filters.some((filter) => filter.isSelected()),

  selectedFiltersFormatted: (state: ProductsState) => {
    const selectedFilters: any[] = [];
    const { t } = useI18n();

    state.filters
      .filter((filter) => filter.isSelected())
      // hide the category selection from the selected filters
      .filter(({ visible, code }) => visible && code !== FilterCode.allCategories)
      .forEach((filter) => {
        const filterType = camelCase(ProductFilterTypes[filter.type]);

        if (filter.type === ProductFilterTypes.PriceSlider) {
          const fromValue: any = (filter as ProductFilterPriceSlider).getFromValue();
          const toValue: any = (filter as ProductFilterPriceSlider).getToValue();

          const label = filter.label
            .replace('{from}', (filter as ProductFilterPriceSlider).getFromFormattedValue() as any)
            .replace('{to}', (filter as ProductFilterPriceSlider).getToFormattedValue() as any);

          selectedFilters.push({
            filterCode: filter.code,
            filterName: filter.name,
            filterLabel: label,
            filterType,
            filterTrackingValue: `${fromValue}-${toValue}`,
          });
        } else if (filter.type === ProductFilterTypes.Checkbox) {
          selectedFilters.push({
            filterCode: filter.code,
            filterName: filter.name,
            filterLabel: filter.displayName,
            filterType,
          });
          // Here: Locations filter is shown as a single "button"
        } else if (filter.code === FilterCode.locations) {
          let filterTrackingValue = '';

          if (state.locationFilterTab === LocationFilterTabs.Area && state.locationFilterParams) {
            filterTrackingValue = `${state.locationFilterParams.search}_${state.locationFilterParams.distance
              .replace(/\s/g, '')
              .toLowerCase()}`;
          } else if (state.dealerFilterInfo) {
            filterTrackingValue = (filter.value?.[0] || '') as string;
          }

          selectedFilters.push({
            filterCode: filter.code,
            filterName: filter.name,
            filterLabel: t('dcp.srp.filters.locationSearch.dealersFound', {
              dealerNumberFound: filter.value.length,
            }),
            filterValue: filter.value,
            filterType,
            filterTrackingValue,
          });
        } else if (filter.type === ProductFilterTypes.SingleSelect || filter.type === ProductFilterTypes.MultiSelect) {
          filter.value.forEach((value: unknown) => {
            const selectedOption = ((filter.options as IProductFilterOptionDTO[]) ?? []).find(
              (option: IProductFilterOptionDTO) => option.value === value,
            );

            if (selectedOption) {
              selectedFilters.push({
                filterCode: filter.code,
                filterName: filter.name,
                filterLabel: selectedOption.name,
                filterValue: selectedOption.value,
                filterType,
              });
            }
          });
        } else if (filter.type === ProductFilterTypes.Slider) {
          const fromValue: any = (filter as ProductFilterSlider).getFromValue();
          const toValue: any = (filter as ProductFilterSlider).getToValue();
          const label = filter.label.replace('{from}', fromValue).replace('{to}', toValue);

          selectedFilters.push({
            filterCode: filter.code,
            filterName: filter.name,
            filterLabel: label,
            filterType,
            filterTrackingValue: `${fromValue}-${toValue}`,
          });
        }
      });

    return selectedFilters;
  },

  sortOptions: (state: ProductsState): DropdownOption[] =>
    state.sortOptions.map((sortOption) => ({
      label: getTranslationFallback(
        // Remove potential "-b2b" or "-b2c" suffix for translation
        `dcp.search.sorting.${sortOption.code.replace(/-(b2c|b2b)$/, '')}`,
        sortOption.name,
      ),
      value: sortOption.code,
      testId: sortOption.code,
    })),

  selectedTab: (state: ProductsState) => (state.selectedCategory.endsWith('vans') ? 'vans' : 'cars'),

  totalResults: (state: ProductsState) => state.paging.totalResults,

  totalResultsLimited: (state: ProductsState) => {
    const {
      paging: { totalResults },
    } = state;
    const {
      srp: {
        products: { countThreshold: thresholdCountLabel, thresholdTemplate },
      },
    } = infuse('config');

    if (!totalResults) return '0';

    if (thresholdCountLabel && thresholdTemplate && totalResults > thresholdCountLabel) {
      return thresholdTemplate.replace('{n}', thresholdCountLabel.toString());
    }

    return String(totalResults);
  },
};

export type ProductsSelectors = typeof selectors;

export const productsSelectors = createSelectors<ProductsSelectors>(productsStore, connectionName, selectors);

if (!productsStore) {
  // Storybook Workaround
  const init = setInterval(() => {
    if (productsStore) {
      clearInterval(init);
    } else {
      Object.assign(productsSelectors, createSelectors<ProductsSelectors>(productsStore, connectionName, selectors));
    }
  }, 200);
}
